import { memo, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import {
    useCreateDashboardStore,
    ColorPicker,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
    UnitPicker,
} from "..";
import DashboardCreateSelectSensorModal from "../modal/SelectDevices";
import { COLORS } from "constant";
import { displaySensorType } from "utils/functions";

import { BlueText } from "components/common";
import { omit } from "lodash";
import { DeviceSelected, WidgetItem } from "types";
import { ChartSource, ChartType } from "constant/chart";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const CreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: true,
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Widget name",
        },
        {
            key: "sensors_count",
            name: "No. of Sensors",
            render: (data: any) => data.attributes.length,
        },
    ],
    transform: (data: any, widgetType: WidgetType) => {
        return {
            attributes: data.attributes.map((d: DeviceSelected) => ({
                color: d.color,
                device_id: d.ldsuUID,
                gateway_id: d.gatewayId,
                name: d.sensorType,
                said: String(d.said),
                show_alert: false,
                time_series: getMQTTQueryByDevice(d),
                unit: d.unit,
            })),
            source: ChartSource.MULTIPLE_SENSORS,
            chart: ChartType.CARD_MULTIPLE_LINES,
            name: data.chart_name,
        };
    },
    validate() {
        const validates = this.data.map((item) => ({
            path: "",
            isValid:
                item.chart_name &&
                CHART_NAME_PATTERN.test(item.chart_name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        return validates;
    },
};

export const itemQuantityHelper = (widgetConfig: {
    minItems?: number;
    maxItems?: number;
}) => {
    const { minItems, maxItems } = widgetConfig;
    if (minItems && maxItems) {
        return `Min ${minItems} and max ${maxItems} sensors`;
    } else if (minItems) {
        return `Min ${minItems} sensors`;
    } else if (maxItems) {
        return `Max ${maxItems} sensors`;
    } else {
        return "";
    }
};

const CreateCardMultiple = ({ widget }: { widget: WidgetItem }) => {
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<{
        [key: string]: DeviceSelected;
    }>({});

    const widgetConfig = useMemo(() => {
        return getWidgetConfigByWidgetType(widget.widgetType);
    }, [widget?.widgetType]);

    const [chartName, setChartName] = useState(
        widgetConfig.defaultWidgetName || ""
    );

    useEffect(() => {
        const data = [
            {
                chart_name: chartName,
                attributes: Object.values(selectedDevices),
            },
        ];

        useCreateDashboardStore.setState({
            createData: {
                ...CreateData,
                data: data,
                multiple: true,
            },
        });
    }, [selectedDevices, chartName, widgetConfig]);

    const onRemove = (device: DeviceSelected) => {
        setSelectedDevices((prev) => omit(prev, device.deviceId));
    };

    const onChange = (
        device: DeviceSelected,
        changes: Partial<DeviceSelected>
    ) => {
        const device_ = { ...device, ...changes };
        setSelectedDevices((prev) => ({
            ...prev,
            [device.deviceId]: device_,
        }));
    };

    return (
        <>
            <Row>
                <Col sm="12">
                    <Form.Group>
                        <Form.Label>Widget Name</Form.Label>
                        <Form.Control
                            name="name"
                            type="text"
                            value={chartName}
                            placeholder="Widget Name"
                            onChange={(event) =>
                                setChartName(event.target.value)
                            }
                        />
                    </Form.Group>

                    <div className="mt-2">
                        <Button
                            className="mr-2"
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        <BlueText>
                            ({itemQuantityHelper(widgetConfig)})
                        </BlueText>
                    </div>
                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        allowSensorFilter={true}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        onSave={(event, data) => {
                            setSelectedDevices(
                                Object.values(data)
                                    .map((item, index: number) => ({
                                        ...item,
                                        name: item.sensorName,
                                        color: selectedDevices[item.deviceId]?.color ?? COLORS[index],
                                        alert: false,
                                    }))
                                    .pluck("deviceId")
                            );
                        }}
                        deviceType="Sensor"
                        maxDevices={widgetConfig.maxItems}
                    />
                </Col>
            </Row>
            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    {!!Object.values(selectedDevices).length && (
                        <div className="table-head">
                            <Row className="no-checkbox">
                                <Col md={2}>Sensor</Col>
                                <Col md={2}>Type</Col>
                                <Col xs={12} md={2}>
                                    {GATEWAY_LABEL}
                                </Col>
                                <Col md={2}>{LDSU_UUID_LABEL}</Col>
                                <Col md={1}>Unit</Col>
                                <Col md={2} className="text-center">
                                    Color
                                </Col>
                                <Col sm={1}></Col>
                            </Row>
                        </div>
                    )}
                    {Object.values(selectedDevices).map((device, index) => (
                        <div
                            key={device.deviceId}
                            id={device.deviceId}
                            className="table-row bg-secondary"
                        >
                            <Row className="no-checkbox">
                                <Col md={2} className="mb-md-0 mb-1">
                                    {device.sensorName}
                                </Col>
                                <Col md={2}>{displaySensorType(device)}</Col>
                                <Col
                                    xs={12}
                                    md={2}
                                    className="text-truncate mb-1 mb-md-0"
                                >
                                    <span title={device.gatewayName}>
                                        {device.gatewayName}
                                    </span>
                                </Col>
                                <Col md={2} className="text-truncate mb-md-1">
                                    <span title={device.ldsuUID}>
                                        {device.ldsuUID}
                                    </span>
                                </Col>
                                <Col
                                    xs={12}
                                    md={1}
                                    className="mb-md-0 mb-1 mb-md-0"
                                >
                                    <UnitPicker
                                        data={
                                            device.unitOptions || [device.unit]
                                        }
                                        defaultValue={device.unit}
                                        onChange={(_event: any, unit: string) =>
                                            onChange(device, { unit: unit })
                                        }
                                    />
                                </Col>
                                <Col
                                    xs={12}
                                    sm={2}
                                    className="d-md-flex justify-content-center mb-1 mb-md-0"
                                >
                                    <ColorPicker
                                        defaultValue={device.color as string}
                                        onChange={(event, color) =>
                                            onChange(device, { color: color })
                                        }
                                    />
                                </Col>
                                <Col
                                    xs={12}
                                    md={1}
                                    className="d-flex justify-content-end  mb-1 mb-md-0"
                                >
                                    <CloseOutlined
                                        onClick={() => onRemove(device)}
                                        className="cursor-pointer mr-2 text-primary-red-1"
                                    />
                                </Col>
                            </Row>
                        </div>
                    ))}
                </Col>
            </Row>
        </>
    );
};

export default memo(CreateCardMultiple);
